import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
  markBulkRead,
  deleteNotification,
  deleteBulkNotifications,
  clearAllNotifications,
} from "../../data/redux/slices/NotificationSlice";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

// ─── Attachment icon helper ───────────────────────────────────────────────────
const AttachIcon = ({ type }) => {
  if (type === "image") return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  );
  if (type === "document") return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
    </svg>
  );
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function NotificationModal({ notif, onClose, onDelete }) {
  if (!notif) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: 14, maxWidth: 560, width: "90%",
          maxHeight: "80vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 4 }}>{notif.title}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {notif.category && notif.category !== "general" && (
                <span style={{
                  ...(notif.category === "missed_call" ? { background: "#fee2e2", color: "#dc2626" } : { background: "#ede9fe", color: "#6d28d9" }),
                  fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 12,
                }}>
                  {notif.category === "missed_call" ? "Missed Call" : notif.category}
                </span>
              )}
              <span style={{ color: "#9ca3af", fontSize: 12 }}>{dayjs(notif.createdAt).format("MMM D, YYYY h:mm A")}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => { onDelete(notif._id); onClose(); }}
              title="Delete"
              style={{ background: "#fee2e2", border: "none", borderRadius: 6, padding: "5px 8px", cursor: "pointer", color: "#ef4444" }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
              </svg>
            </button>
            <button
              onClick={onClose}
              style={{ background: "#f3f4f6", border: "none", borderRadius: 6, padding: "5px 8px", cursor: "pointer", color: "#6b7280" }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {notif.description && (
            <p style={{ color: "#374151", fontSize: 14, marginBottom: 12, lineHeight: 1.6 }}>{notif.description}</p>
          )}
          {notif.body && (
            <div
              style={{ color: "#374151", fontSize: 14, lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: notif.body }}
            />
          )}

          {/* Attachments */}
          {notif?.attachments?.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Attachments</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {notif?.attachments?.map((a, i) => (
                  <div key={i} style={{ display: "flex", gap: 8 }}>
                    {a.type === "image" ? (
                      <a href={a.url} target="_blank" rel="noreferrer">
                        <img src={a.url} alt={a.name || "attachment"} style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, objectFit: "contain", border: "1px solid #e5e7eb" }} />
                      </a>
                    ) : (
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
                          padding: "8px 12px", color: "#6366f1", fontSize: 13, textDecoration: "none",
                        }}
                      >
                        <AttachIcon type={a.type} />
                        {a.name || a.url}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function NotificationDropdown() {
  const dispatch = useDispatch();
  const { items, unreadCount, hasMore, loading, loadingMore, page } = useSelector((s) => s.notifications);


// const [notifications, setNotifications] = useState({
//   items: [
//     {
//       _id: "1",
//       title: "New Message",
//       message: "You received a new message from John",
//       description:"hello 1",
//       isRead: false,
//       createdAt: new Date().toISOString(),
//     },
//     {
//       _id: "2",
//       title: "Order Update",
//       message: "Your order has been delivered",
//        description:"hello 2",
//       isRead: true,
//       createdAt: new Date().toISOString(),
//     },
//   ],
//   unreadCount: 1,
//   hasMore: true,
//   loading: false,
//   loadingMore: false,
//   page: 1,
// });

// const { items, unreadCount, hasMore, loading, loadingMore, page } = notifications;
  
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeNotif, setActiveNotif] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const POLL_INTERVAL = 60_000; // 1 min

  // ── Initial load + polling ─────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchNotifications({ page: 1 }));
    const interval = setInterval(() => {
      dispatch(fetchUnreadCount());
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [dispatch]);

  // ── Close on outside click ─────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSelectedIds(new Set());
        setConfirmClearAll(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Infinite scroll ────────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || loadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      dispatch(fetchNotifications({ page: page + 1, append: true }));
    }
  }, [dispatch, hasMore, loadingMore, page]);

  // ── Selection helpers ──────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(items.map((n) => n._id)));
  const clearSelection = () => setSelectedIds(new Set());

  const handleOpen = () => {
    setOpen((v) => !v);
    setSelectedIds(new Set());
    setConfirmClearAll(false);
    if (!open) dispatch(fetchNotifications({ page: 1 }));
  };

  const handleClickNotif = (notif) => {
    if (!notif.isRead) dispatch(markAsRead(notif._id));
    setActiveNotif(notif);
  };

  return (
    <>
      {/* Bell button */}
      <div ref={dropdownRef} style={{ position: "relative", background:"white",boxShadow:"rgba(0, 0, 0, 0.1) 0px 1px 3px", borderRadius:"4px" }}>
        <button
          onClick={handleOpen}
          style={{
            position: "relative", background: "transparent", border: "none",
            cursor: "pointer", padding: "6px 8px", borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#6b7280",
          }}
          title="Notifications"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 01-3.46 0"/>
          </svg>
          {unreadCount > 0 && (
            <span style={{
              position: "absolute", top: 2, right: 2,
              background: "#ef4444", color: "#fff",
              fontSize: 9, fontWeight: 700, lineHeight: 1,
              minWidth: 16, height: 16, borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "0 3px",
            }}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown panel */}
        {open && (
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            width: 380, maxHeight: 540,
            background: "#fff", borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
            border: "1px solid #e5e7eb",
            display: "flex", flexDirection: "column",
            zIndex: 9000,
          }}>
            {/* ── Panel header ── */}
            <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Notifications</span>
                  {unreadCount > 0 && (
                    <span style={{ background: "#6366f1", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>
                      {unreadCount} new
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {unreadCount > 0 && (
                    <button
                      onClick={() => dispatch(markAllAsRead())}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#6366f1", fontSize: 12, fontWeight: 600 }}
                    >
                      Mark all read
                    </button>
                  )}
                  {items?.length > 0 && (
                    confirmClearAll ? (
                      <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <span style={{ fontSize: 12, color: "#ef4444" }}>Clear all?</span>
                        <button
                          onClick={() => { dispatch(clearAllNotifications()); setConfirmClearAll(false); }}
                          style={{ background: "#fee2e2", border: "none", borderRadius: 5, padding: "2px 8px", cursor: "pointer", color: "#ef4444", fontSize: 12, fontWeight: 600 }}
                        >Yes</button>
                        <button
                          onClick={() => setConfirmClearAll(false)}
                          style={{ background: "#f3f4f6", border: "none", borderRadius: 5, padding: "2px 8px", cursor: "pointer", color: "#6b7280", fontSize: 12 }}
                        >No</button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmClearAll(true)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 12, fontWeight: 600 }}
                      >
                        Clear all
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Bulk action bar */}
              {selectedIds.size > 0 && (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, background: "#f5f3ff", borderRadius: 8, padding: "6px 10px" }}>
                  <span style={{ fontSize: 12, color: "#6d28d9", fontWeight: 600 }}>{selectedIds.size} selected</span>
                  <button
                    onClick={() => dispatch(markBulkRead([...selectedIds])).then(() => clearSelection())}
                    style={{ background: "#ede9fe", border: "none", borderRadius: 6, padding: "3px 10px", cursor: "pointer", color: "#6d28d9", fontSize: 12, fontWeight: 600 }}
                  >Mark read</button>
                  <button
                    onClick={() => dispatch(deleteBulkNotifications([...selectedIds])).then(() => clearSelection())}
                    style={{ background: "#fee2e2", border: "none", borderRadius: 6, padding: "3px 10px", cursor: "pointer", color: "#ef4444", fontSize: 12, fontWeight: 600 }}
                  >Delete</button>
                  <button
                    onClick={selectAll}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 12, marginLeft: "auto" }}
                  >All</button>
                  <button
                    onClick={clearSelection}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 12 }}
                  >Clear</button>
                </div>
              )}
            </div>

            {/* ── List ── */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              style={{ flex: 1, overflowY: "auto", minHeight: 0, maxHeight: 420 }}
            >
              {loading ? (
                <div style={{ padding: "32px 0", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"
                    style={{ animation: "spin 1s linear infinite", display: "block", margin: "0 auto 8px" }}>
                    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/>
                  </svg>
                  Loading…
                </div>
              ) : items?.length === 0 ? (
                <div style={{ padding: "36px 20px 40px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  {/* Animated bell illustration */}
                  <div style={{ position: "relative", width: 72, height: 72 }}>
                    <svg viewBox="0 0 72 72" width="72" height="72" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <style>{`
                        @keyframes bellSwing {
                          0%,100%{transform-origin:50% 8px;transform:rotate(0deg)}
                          20%{transform:rotate(12deg)}
                          40%{transform:rotate(-10deg)}
                          60%{transform:rotate(6deg)}
                          80%{transform:rotate(-4deg)}
                        }
                        @keyframes badgePulse {
                          0%,100%{transform:scale(1)}
                          50%{transform:scale(1.18)}
                        }
                        .bell-group { animation: bellSwing 3s ease-in-out infinite; transform-origin: 50% 8px; }
                        .badge-dot { animation: badgePulse 3s ease-in-out infinite; transform-origin: 55px 18px; }
                      `}</style>
                      <circle cx="36" cy="36" r="36" fill="#f5f3ff"/>
                      <g className="bell-group">
                        <path d="M36 14a14 14 0 00-14 14v8l-3 5h34l-3-5v-8A14 14 0 0036 14z" fill="#c4b5fd" stroke="#7c3aed" strokeWidth="1.5" strokeLinejoin="round"/>
                        <path d="M32 41a4 4 0 008 0" fill="#7c3aed"/>
                        <line x1="36" y1="14" x2="36" y2="10" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"/>
                      </g>
                      <circle className="badge-dot" cx="55" cy="18" r="6" fill="#e5e7eb" stroke="#fff" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#374151" }}>You're all caught up!</div>
                  <div style={{ color: "#9ca3af", fontSize: 13, lineHeight: 1.5, maxWidth: 220 }}>
                    No notifications right now. We'll let you know when something happens.
                  </div>
                </div>
              ) : items?.map((notif) => {
                const isSelected = selectedIds.has(notif._id);
                return (
                  <div
                    key={notif._id}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 10,
                      padding: "11px 14px",
                      borderBottom: "1px solid #f9fafb",
                      background: isSelected ? "#f5f3ff" : notif.isRead ? "#fff" : "#fafaf7",
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f9fafb"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "#f5f3ff" : notif.isRead ? "#fff" : "#fafaf7"; }}
                  >
                    {/* Checkbox */}
                    <div
                      onClick={(e) => { e.stopPropagation(); toggleSelect(notif._id); }}
                      style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 2,
                        border: isSelected ? "2px solid #6366f1" : "2px solid #d1d5db",
                        background: isSelected ? "#6366f1" : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      {isSelected && (
                        <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                          <polyline points="2 6 5 9 10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>

                    {/* Unread dot */}
                    <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 5, background: notif.isRead ? "transparent" : "#6366f1" }} />

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }} onClick={() => handleClickNotif(notif)}>
                      <div style={{ fontSize: 13, fontWeight: notif.isRead ? 500 : 700, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {notif.title}
                      </div>
                      {notif.description && (
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {notif.description}
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                        {notif.category && notif.category !== "general" && (() => {
                          const catStyles = notif.category === "missed_call"
                            ? { background: "#fee2e2", color: "#dc2626" }
                            : { background: "#ede9fe", color: "#6d28d9" };
                          const catLabel = notif.category === "missed_call" ? "Missed Call" : notif.category;
                          return (
                            <span style={{ ...catStyles, fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 8 }}>
                              {catLabel}
                            </span>
                          );
                        })()}
                        {notif?.attachments?.length > 0 && (
                          <span style={{ color: "#9ca3af", fontSize: 10, display: "flex", alignItems: "center", gap: 2 }}>
                            <AttachIcon type={notif.attachments[0].type} />
                            {notif?.attachments?.length}
                          </span>
                        )}
                        <span style={{ color: "#9ca3af", fontSize: 10 }}>{dayjs(notif.createdAt).fromNow()}</span>
                      </div>
                    </div>

                    {/* Mark as read button (only for unread notifications) */}
                    {!notif.isRead && (
                      <button
                        onClick={(e) => { e.stopPropagation(); dispatch(markAsRead(notif._id)); }}
                        title="Mark as read"
                        style={{
                          background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 5,
                          color: "#d1d5db", flexShrink: 0,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "#6366f1"; e.currentTarget.style.background = "#ede9fe"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "#d1d5db"; e.currentTarget.style.background = "none"; }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Load more spinner */}
              {loadingMore && (
                <div style={{ padding: "12px 0", textAlign: "center" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"
                    style={{ animation: "spin 1s linear infinite" }}>
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/>
                  </svg>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {activeNotif && (
        <NotificationModal
          notif={activeNotif}
          onClose={() => setActiveNotif(null)}
          onDelete={(id) => dispatch(deleteNotification(id))}
        />
      )}
    </>
  );
}
